import { UseQueryOptions, useMutation, useQuery } from 'react-query';
import { RemoteInterviewPracticesPooledQuestionsRepo } from '@repository/interviewPractices/pooledQuestions/PooledQuestionsRepo';
import PooledCustomQuestionCreateRq from '@domain/rq/pass/interview/PooledCustomQuestionCreateRq';
import { CompanySearchVO } from '@domain/vo/pass/interview/company/CompanySearchVO';
import { CompanyQuestionListVO } from '@domain/vo/pass/interview/question/CompanyQuestionVO';
import { FrequencyQuestionListVO } from '@domain/vo/pass/interview/question/FrequencyQuestionVO';
import { MyselfQuestionListVO } from '@domain/vo/pass/interview/question/MyselfQuestionVO';
import { JobTypeCode } from '@domain/constant/v1/jobType';
import { JobGroupCode } from '@domain/constant/filterType';

const remoteInterviewPracticesPooledQuestionsRepo = new RemoteInterviewPracticesPooledQuestionsRepo();

export const keys = ['interview-practices', 'pooled-questions'];
export const usePoliciesKeys = [...keys, 'policies'];
export const useCustomQuestionListKeys = [...keys, 'customs'];

const fetchPolicies = async () => {
  const { data } = await remoteInterviewPracticesPooledQuestionsRepo.fetchPolicies();
  return data;
};

export const usePolicies = (options?: UseQueryOptions) => {
  return useQuery(usePoliciesKeys, fetchPolicies, {
    staleTime: Infinity,
    cacheTime: Infinity,
    ...(options as any),
  });
};

const fetchCompanyQuestionList = async (
  selectedCompanySn: number,
  jobTypeCodes: JobTypeCode[],
  jobGroupCodes: JobGroupCode[],
) => {
  const { data } = await remoteInterviewPracticesPooledQuestionsRepo.fetchCompanyQuestionList(
    selectedCompanySn,
    jobTypeCodes,
    jobGroupCodes,
  );
  return new CompanyQuestionListVO(data);
};

export const useCompanyQuestionList = (
  selectedCompanySn: number | null,
  jobTypeCodes: JobTypeCode[],
  jobGroupCodes: JobGroupCode[],

  options?: UseQueryOptions,
) => {
  return useQuery(
    [...keys, 'companies', selectedCompanySn, jobTypeCodes, jobGroupCodes],
    () => fetchCompanyQuestionList(selectedCompanySn as number, jobTypeCodes, jobGroupCodes),
    {
      keepPreviousData: true,
      enabled: selectedCompanySn !== null,
      ...(options as any),
    },
  );
};

const fetchFrequencyQuestionList = async () => {
  const { data } = await remoteInterviewPracticesPooledQuestionsRepo.fetchFrequencyQuestionList();
  return new FrequencyQuestionListVO(data);
};

export const useFrequencyQuestionList = (options?: UseQueryOptions) => {
  return useQuery([...keys, 'commons'], fetchFrequencyQuestionList, {
    ...(options as any),
  });
};

const fetchCompanyList = async () => {
  const { data } = await remoteInterviewPracticesPooledQuestionsRepo.fetchCompanyList();
  return new CompanySearchVO(data);
};

export const useCompanyList = (options?: UseQueryOptions) => {
  return useQuery([...keys, 'companies', 'list'], fetchCompanyList, {
    ...(options as any),
  });
};

const createCustomQuestion = async (question: string) => {
  const rq = new PooledCustomQuestionCreateRq(question);
  return await remoteInterviewPracticesPooledQuestionsRepo.createCustomQuestion(rq);
};

const fetchCustomQuestionList = async () => {
  const { data } = await remoteInterviewPracticesPooledQuestionsRepo.fetchCustomQuestionList();
  return new MyselfQuestionListVO(data);
};

export const useCustomQuestionList = (options?: UseQueryOptions) => {
  return useQuery([...keys, 'customs'], fetchCustomQuestionList, {
    ...(options as any),
  });
};

export const useCreateCustomQuestion = (onSuccess?: () => void) => {
  return useMutation(createCustomQuestion, { onSuccess });
};

const deleteCustomQuestion = async (questionSn: number) => {
  return await remoteInterviewPracticesPooledQuestionsRepo.deleteCustomQuestion(questionSn);
};

export const useDeleteCustomQuestion = (onSuccess?: () => void) => {
  return useMutation(deleteCustomQuestion, { onSuccess });
};

const fetchAccPracticeQuestions = async (code: string) => {
  const { data } = await remoteInterviewPracticesPooledQuestionsRepo.fetchAccPracticeQuestions(code);
  return data;
};

export const useAccPracticeQuestions = (code: string, options?: UseQueryOptions) => {
  return useQuery([...keys, 'acc', code], () => fetchAccPracticeQuestions(code), {
    ...(options as any),
  });
};
